import { assertBannerActionable, statesProblem, t, type BannerAction, type BannerTone, type IconMeaning } from '@bms2/shared';
import type { ReactNode } from 'react';
import { Icon } from '../Icon';
import { drawnMoney } from '../Money';
import styles from './AlertBanner.module.css';

/**
 * Alert Banner (piece 3): a page-level message in one of the four status
 * tones or Info (decision 1218), with an optional dismiss and an optional action link. It is distinct
 * from a field error, which stays inline at the field, and from the Saved
 * Confirmation, the one note that fades (decision 1325). A banner stating a
 * problem and offering no action is refused.
 *
 * 🔒 Every banner is one shape (decision 1326): a tinted panel with the tone's
 * bar on its left edge and a status icon before the words, so a phone that
 * shows the colour poorly still shows the kind of message.
 *
 * 🔒 The message is a node, so a page puts the Money component inside it where
 * the sentence carries a figure (decision 0717). A message handed over as text
 * is drawn through the component's text form, so a negative amount a page wrote
 * into a sentence still reads red.
 */
interface AlertBannerBase {
  message: ReactNode;
  action?: BannerAction;
  onDismiss?: () => void;
}

/**
 * 🔒 The page urging rather than stating a fault (decision 1485): only the Warning
 * tone offers `urging`, and then it needs no action link. Every other tone cannot
 * carry the flag, and the Error tone is refused by `assertBannerActionable` too.
 */
export type AlertBannerProps = AlertBannerBase &
  (
    | { tone: 'warning'; urging?: boolean }
    | { tone: Exclude<BannerTone, 'warning'>; urging?: never }
  );

/** 🔒 The four status tones and Info, the page speaking rather than something being wrong (decision 1218). */
export const alertBannerDocumentedStates = ['good', 'warning', 'error', 'inactive', 'info'] as const;

/** 🔒 The icon each tone draws before its words (decision 1326): a tick, a triangle, a circle-alert, and an info mark for the two tones that state no problem. */
export const bannerIcon: Record<BannerTone, IconMeaning> = {
  good: 'bannerGood',
  warning: 'bannerWarning',
  error: 'bannerError',
  inactive: 'bannerInfo',
  info: 'bannerInfo',
};

export function AlertBanner({ tone, message, action, onDismiss, urging }: AlertBannerProps) {
  assertBannerActionable(tone, action, urging === true);
  return (
    <div className={`${styles.banner} ${styles[tone]}`} role={statesProblem(tone) ? 'alert' : 'status'} data-banner={tone}>
      <span className={styles.icon} data-banner-icon={bannerIcon[tone]}>
        <Icon meaning={bannerIcon[tone]} size="inline" />
      </span>
      <span className={styles.message}>{drawnMoney(message)}</span>
      {action ? (
        <button type="button" className={styles.action} onClick={action.onAction} data-banner-action="true">
          {action.label}
        </button>
      ) : null}
      {onDismiss ? (
        <button type="button" className={styles.dismiss} aria-label={t('banner.dismiss')} onClick={onDismiss} data-banner-dismiss="true">
          <Icon meaning="remove" size="header" />
        </button>
      ) : null}
    </div>
  );
}
